import { useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Hourglass, Layers, Loader2, Lock } from "lucide-react";
import { MODULE_LABELS } from "@/components/admin/ShopModulesEditor";

const ALWAYS = ["Ordens de serviço", "Clientes", "Fotos", "WhatsApp manual"];

function AskButton({ body, pending, testid, label = "Quero mudar de plano" }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(!!pending);
  const ask = async () => {
    setBusy(true);
    try { await api.post("/my-plan/request", body); setDone(true); toast.success("Pedido enviado — o suporte vai falar com você"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (done) { return <span data-testid={`${testid}-sent`} className="inline-flex items-center gap-1 text-[11px] font-bold text-sky-300"><Hourglass className="h-3.5 w-3.5" /> Pedido enviado</span>; }
  return <Button data-testid={testid} onClick={ask} disabled={busy} className="h-9 bg-green-brand hover:bg-green-brandDark text-white font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : label}</Button>;
}

export default function OwnerPlan() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/my-plan").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!d) { return <Loader2 className="h-6 w-6 animate-spin text-green-brand" />; }
  const on = Object.keys(MODULE_LABELS).filter((k) => d.modules[k] !== false);
  const off = Object.keys(MODULE_LABELS).filter((k) => d.modules[k] === false);
  return (
    <div data-testid="owner-plan" className="space-y-5 animate-fade-up">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Configurações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Meu plano</h1>
      </div>
      <section className="rounded-2xl border border-green-900/60 bg-card2 p-5 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-zinc-400 text-xs"><Layers className="h-4 w-4 text-green-neon" /> Plano atual</div>
            <div data-testid="my-plan-name" className="font-display text-2xl font-black uppercase text-zinc-100">{d.plan?.name || "Personalizado"}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-zinc-400">Mensalidade</div>
            <div data-testid="my-plan-amount" className="font-display text-2xl font-black text-green-neon">{brl(d.amount || 0)}<span className="text-xs text-zinc-500 font-normal"> /mês</span></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div data-testid="my-plan-included">
            <div className="text-[11px] font-mono-caps text-green-300 mb-1">Incluso</div>
            {[...ALWAYS, ...on.map((k) => MODULE_LABELS[k])].map((l) => <div key={l} className="flex items-center gap-2 text-sm text-zinc-200"><Check className="h-4 w-4 text-green-400" />{l}</div>)}
          </div>
          <div data-testid="my-plan-blocked">
            <div className="text-[11px] font-mono-caps text-zinc-500 mb-1">Bloqueado no seu plano</div>
            {off.length ? off.map((k) => <div key={k} className="flex items-center gap-2 text-sm text-zinc-500"><Lock className="h-4 w-4" />{MODULE_LABELS[k]}</div>) : <p className="text-sm text-zinc-500">Nada — você tem todos os módulos.</p>}
          </div>
        </div>
        <AskButton body={{}} testid="my-plan-ask" />
      </section>
      <section className="space-y-2">
        <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Outros planos</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {d.plans.map((p) => (
            <div key={p.id} data-testid={`plan-option-${p.id}`} className={`rounded-2xl border p-4 space-y-2 ${p.id === d.plan?.id ? "border-green-700 bg-green-950/20" : "border-zinc-800 bg-zinc-950/60"}`}>
              <div className="flex justify-between"><span className="font-display text-lg font-bold text-zinc-100">{p.name}</span><span className="font-bold text-zinc-200">{brl(p.price)}</span></div>
              <p className="text-xs text-zinc-400">{[...ALWAYS, ...p.modules.map((k) => MODULE_LABELS[k].split(" (")[0])].join(" · ")}</p>
              {p.id === d.plan?.id ? <span className="text-[11px] font-bold text-green-300">Seu plano</span> : <AskButton body={{ plan_id: p.id }} pending={d.pending[p.id]} testid={`plan-option-${p.id}-ask`} label="Quero este plano" />}
            </div>
          ))}
        </div>
      </section>
      <section className="space-y-2">
        <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Adicionais</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {d.addons.map((a) => (
            <div key={a.id} data-testid={`my-addon-${a.slug || a.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-2">
              <div className="flex justify-between"><span className="font-bold text-zinc-100">{a.name}</span><span className="text-zinc-200">{brl(a.price)}<span className="text-[11px] text-zinc-500">/mês</span></span></div>
              {a.active ? <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-300"><Check className="h-3.5 w-3.5" /> Ativo</span>
                : <AskButton body={{ addon_id: a.id }} pending={d.pending[a.id]} testid={`my-addon-${a.slug || a.id}-ask`} label="Quero contratar" />}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
